import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import type { BonanzaPromoDataApi } from '@/api/cms';
import { ImageComponent } from '@/components/Image';
import { useDevice } from '@/hooks/device';
import { useBonanzaPromo } from '@/modules/promotions';
import { BonanzaPromoStatus } from '@/modules/promotions/helpers';
import { useGetCommonStore } from '@/store';
import { CtaButton } from '../CtaButton';
import { ProgressBar } from '../ProgressBar';
import { StatusLabel } from '../StatusLabel';
import { Terms } from '../Terms';
import { ScPromoStatus } from './styled';

type PromoStatusProps = Pick<BonanzaPromoDataApi, 'bonanzaStatus'>;

export const PromoStatus: React.FC<PromoStatusProps> = ({ bonanzaStatus }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['common', 'bonanzaPromo']);

  const { isMobileDevice } = useDevice();
  const { isBonusIssued, nextIterationStartDay, promoBonus } =
    useBonanzaPromo();
  const useCommonStore = useGetCommonStore();

  const { userBonanzaGroup, userBonanzaGroupKey } = useCommonStore(
    useShallow((state) => ({
      userBonanzaGroup: state.userBonanzaGroup,
      bonanzaReward: state.bonanzaReward,
      userBonanzaGroupKey: state.userBonanzaGroupKey,
    }))
  );

  const imgSize = useMemo(() => {
    const defaultSize = {
      width: isMobileDevice ? 300 : 400,
      height: isMobileDevice ? 300 : 400,
    };

    return defaultSize;
  }, [isMobileDevice]);

  const imgByStatus = useMemo(
    () =>
      bonanzaStatus.find(
        ({ promoStatus }) => promoStatus === userBonanzaGroupKey
      ),
    [bonanzaStatus, userBonanzaGroupKey]
  );

  const termsTranslates = useMemo(() => {
    if (!promoBonus) return;

    const { wager, amount } = promoBonus;

    return {
      title: t('bonanzaPromo:terms'),
      content: [
        t('bonanzaPromo:termsPoints.bonus', {
          amount,
        }),
        t('bonanzaPromo:termsPoints.expire'),
        t('bonanzaPromo:termsPoints.wager', { wager }),
      ],
      amount,
    };
  }, [t, promoBonus]);

  const isProgressBarVisible = ![
    BonanzaPromoStatus.LEVEL_4,
    BonanzaPromoStatus.PROMO_LOCKED,
    BonanzaPromoStatus.UNLOGGED,
  ].includes(userBonanzaGroup);

  return (
    <ScPromoStatus language={language} status={userBonanzaGroup}>
      <div className="img-wrapper">
        {imgByStatus && (
          <ImageComponent
            {...imgSize}
            alt="bonanza img"
            className="bonanza-img"
            priority
            src={imgByStatus.image?.url || ''}
          />
        )}
      </div>
      <StatusLabel
        nextIterationStartDay={nextIterationStartDay}
        status={userBonanzaGroup}
      />
      {isProgressBarVisible && <ProgressBar />}
      <CtaButton />
      {isBonusIssued && termsTranslates && <Terms {...termsTranslates} />}
    </ScPromoStatus>
  );
};
